<template>
  <div class="tw-p-8">
    <!--
      Dev-only question palette for the v7 followup modal. Click any question to
      jump the modal to that step, then walk the flow from there via the stub
      provider — no backend required. Hosted by views/Playground.vue at /playground.
    -->
    <div
      class="tw-fixed tw-z-[1100] tw-flex tw-max-h-[85vh] tw-w-72 tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-white tw-shadow-2xl tw-ring-1 tw-ring-black/10"
      :style="{ left: pos.x + 'px', top: pos.y + 'px' }"
    >
      <p
        class="tw-flex tw-cursor-move tw-select-none tw-items-center tw-justify-between tw-border-b tw-border-light-gray tw-px-3 tw-py-2 tw-font-atten-bold tw-text-xs tw-uppercase tw-tracking-widest tw-text-dark"
        @mousedown.prevent="startDrag"
      >
        v7 questions · dev
        <span class="tw-text-base tw-leading-none tw-opacity-40">⠿</span>
      </p>
      <div class="tw-overflow-y-auto">
        <button
          v-for="q in questions"
          :key="q.id"
          type="button"
          class="tw-flex tw-w-full tw-flex-col tw-gap-0.5 tw-border-b tw-border-light-gray tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-transition-colors"
          :class="q.id === activeId
            ? 'tw-bg-lavender-purple tw-text-white'
            : 'tw-bg-white tw-text-dark hover:tw-bg-light-gray'"
          @click="jumpTo(q.id)"
        >
          <span class="tw-font-atten-bold">{{ q.id }} · {{ q.answertype }}</span>
          <span class="tw-truncate tw-text-xs tw-opacity-80">{{ label(q) }}</span>
        </button>
      </div>
    </div>

    <FollowupV7
      ref="modal"
      :provider="provider"
      :messages="messages"
      @hide="onHide"
      @complete="onComplete"
      @alert="onAlert"
    />
  </div>
</template>

<script>
import FollowupV7 from '@/components/followup/v7/index.vue';
import { createStubProvider } from '@/components/followup/v7/provider.js';
import v7Questions from './questions.fixture.js';

/**
 * Reusable v7 follow-up preview harness. Imported by views/Playground.vue (the
 * /playground route). Hosts the v7 followup modal with a floating question
 * palette over the static fixture and an in-memory stub provider, so the modal
 * can be exercised offline with no backend.
 */
const STUB_MESSAGE_ID = 'stub-msg';

export default {
  name: 'FollowUpV7Playground',

  components: {
    FollowupV7,
  },

  data() {
    return {
      questions: v7Questions,
      activeId: null,
      pos: { x: 16, y: 16 },
      dragOffset: { x: 0, y: 0 },
      provider: createStubProvider(v7Questions, {
        doctor: {
          firstname: 'Doctor 03 Three',
          lastname: 'Garcia',
          profileUrl: '',
        },
      }),
      messages: [{ id: STUB_MESSAGE_ID, actionstatus: 'pending' }],
    };
  },

  created() {
    /* Stable refs so the window listeners can be removed on drag end / destroy. */
    this.boundDrag = this.onDrag.bind(this);
    this.boundStopDrag = this.stopDrag.bind(this);
  },

  mounted() {
    this.jumpTo(1);
  },

  beforeDestroy() {
    this.stopDrag();
  },

  methods: {
    startDrag(event) {
      this.dragOffset.x = event.clientX - this.pos.x;
      this.dragOffset.y = event.clientY - this.pos.y;
      window.addEventListener('mousemove', this.boundDrag);
      window.addEventListener('mouseup', this.boundStopDrag);
    },

    onDrag(event) {
      this.pos.x = event.clientX - this.dragOffset.x;
      this.pos.y = event.clientY - this.dragOffset.y;
    },

    stopDrag() {
      window.removeEventListener('mousemove', this.boundDrag);
      window.removeEventListener('mouseup', this.boundStopDrag);
    },

    /* Short one-line label for a palette row. */
    label(question) {
      return question.question || question.title || '(no text)';
    },

    async jumpTo(id) {
      const result = await this.$refs.modal.setFollowUpMessage(STUB_MESSAGE_ID, id);
      if (result.ok) {
        this.activeId = id;
        this.$refs.modal.show();
      } else {
        // eslint-disable-next-line no-console
        console.warn('[Playground] could not load question', id, result.message);
      }
    },

    onHide() {
      // eslint-disable-next-line no-console
      console.log('[Playground] modal hidden');
    },
    onComplete() {
      // eslint-disable-next-line no-console
      console.log('[Playground] flow complete');
    },
    onAlert(message) {
      // eslint-disable-next-line no-console
      console.log('[Playground] alert', message);
    },
  },
};
</script>
